/**
 * The one place the console turns a failed request into a sentence an admin can
 * read.
 *
 * Every page used to keep its own copy of this (17 of them), and each fell back
 * to `err.message` — which is Axios's own text, so admins were shown
 * "Network Error", "timeout of 15000ms exceeded" and "Request failed with status
 * code 500". See docs/user-facing-error-messages.md §4.2.
 *
 * Order of preference:
 *  1. the API's own `message` (already written for humans),
 *  2. the per-field validation issues it sent with it,
 *  3. a status / transport fallback — never Axios's text.
 *
 * A 5xx also carries the `correlationId` the API returned, so an admin has a
 * reference to quote to support (§4.5).
 */

interface ApiIssue {
  path?: (string | number)[];
  message?: string;
  label?: string;
  friendly?: string;
}

interface ApiErrorBody {
  code?: string;
  message?: string;
  correlationId?: string;
  details?: { issues?: ApiIssue[] };
}

interface HttpErrorish {
  response?: { status?: number; data?: ApiErrorBody; headers?: Record<string, string> };
  code?: string;
  message?: string;
}

/** Generic server copy that a per-field list would improve on. */
const GENERIC = /^(invalid request|invalid request body|invalid query parameters|invalid input|validation error)\.?$/i;

/** "Email must be a valid email address; Password is required" */
function issuesText(issues: ApiIssue[] | undefined): string | null {
  if (!issues?.length) return null;
  const parts: string[] = [];
  for (const issue of issues) {
    if (issue.friendly) {
      parts.push(issue.friendly);
      continue;
    }
    if (!issue.message) continue;
    const field = issue.label ?? (issue.path?.length ? String(issue.path[issue.path.length - 1]) : null);
    parts.push(field ? `${field}: ${issue.message}` : issue.message);
  }
  return parts.length ? parts.join('; ') : null;
}

/** What went wrong when the API sent no `{ code, message }` body of its own. */
function fallbackFor(err: HttpErrorish): string {
  const status = err.response?.status;
  switch (status) {
    case 400:
    case 422:
      return 'The server rejected this request. Reload the page and try again.';
    case 401:
      return 'You were signed out. Please sign in again.';
    case 403:
      return "You don't have permission to do that.";
    case 404:
      return "That isn't available any more. Reload the page.";
    case 409:
      return 'Someone else changed this first. Reload the page and try again.';
    case 413:
      return 'That file is too large to upload.';
    case 429:
      return 'Too many requests — wait a moment and try again.';
    default:
      break;
  }
  if (status && status >= 500) return 'The server had a problem. Please try again in a moment.';
  if (err.code === 'ECONNABORTED') return 'The request timed out. Check your connection and try again.';
  if (err.code === 'ERR_NETWORK' || !err.response) {
    return "Can't reach the server. Check your connection and try again.";
  }
  return 'Something went wrong. Please try again.';
}

/** Short reference an admin can quote to support when the server 500s. */
function referenceFor(err: HttpErrorish): string | null {
  const status = err.response?.status ?? 0;
  if (status < 500) return null;
  const id = err.response?.data?.correlationId ?? err.response?.headers?.['x-correlation-id'];
  return id ? ` (ref ${String(id).slice(0, 8)})` : null;
}

/** The message for a raw Axios rejection. Used by the interceptor. */
export function messageFor(err: unknown): string {
  if (!err || typeof err !== 'object') return 'Something went wrong. Please try again.';
  const e = err as HttpErrorish;
  const body = e.response?.data;

  let message = body?.message?.trim() || null;
  const issues = issuesText(body?.details?.issues);
  // An older server (or a bare ZodError) can still answer "Invalid request" with
  // the real problem only in `issues` — show that instead of the placeholder.
  if (issues && (!message || GENERIC.test(message))) message = issues;

  return (message ?? fallbackFor(e)) + (referenceFor(e) ?? '');
}

/**
 * What a page shows. Uses the message the response interceptor already worked
 * out, and falls back to computing it here for anything thrown outside the
 * client.
 */
export function extractErr(err: unknown): string {
  const pre = (err as { normalised?: { message?: string } } | null)?.normalised?.message;
  return pre ?? messageFor(err);
}

/** Alias for the pages that imported this name. */
export const extractErrorMessage = extractErr;
